/**
 * 작성자 : 홍선영
 * 날짜 : 2026.06.18
 * 기능 : 센서 종합현황 위젯(dCd 152) 코어 컴포넌트
 *
 * 대시보드(Dashboard)와 상황판(SiteDashboard)에서 공유하며, 페이징 로직은 양쪽 동일하다.
 *  - 페이징 자동전환(autoPlay)은 기본 ON (세로/가로 페이지를 자동 순회)
 *  - refetchInterval 은 데이터 갱신 주기 용도로만 사용(상황판 여부 판별엔 더 이상 페이징이 의존하지 않음)
 *
 * 레이아웃 :
 *  - 2단 그룹 헤더 테이블(대안 ①) 단일 뷰
 *  - 설치 위치 > 5 → 세로 페이징(5개씩)
 *  - 센서 종류(컬럼) > 12 → 가로 페이징(그룹 단위로 묶어 12종씩)
 *  - 폰트는 셀 크기(실측 높이/행 수)에 비례하여 반응형
 *
 * 데이터 : GET /sensor/widget (hCd·sCd) 응답을 sensorRowsToWidgetData 어댑터로 정규화해 사용.
 *          data prop 을 주입하면 API 대신 그 값을 그대로 렌더(테스트/미리보기용).
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { apiGet } from '../../../services/_common';
import IssueGuide from '../../IssueGuide';
import SkeletonTable from '../../SkeletonTable';
import PlayButton from '../PlayButton';
import { SensorWidgetData, SensorWidgetRow } from './types';
import { buildSensorNameMap, buildSensorRangeMap, paginateGroupsByColumns, sensorRowsToWidgetData, SensorNameMap, SensorRangeMap } from './helpers';
import { useElementSize, usePager } from './hooks';
import { SensorStatusRoot } from './styles';
import SensorTableView from './SensorTableView';
import { SENSOR_WIDGET_MOCK, SENSOR_RANGE_MOCK } from './mock';

interface Props {
  name?: string;
  refetchInterval?: number; // 상황판에서 전달되는 데이터 자동 갱신 주기(ms)
  data?: SensorWidgetData; // 주입 시 API 대신 이 값을 렌더(테스트/미리보기용)
}

// 페이징 상수
const TABLE_PAGE_SIZE = 5; // 설치 위치(행) 5개 초과 시 세로 페이징
const MAX_SENSOR_COLS = 12; // 센서 종류(컬럼) 한 페이지 상한 — 12종 초과 시 가로 페이징
const PAGE_INTERVAL = 10000; // 자동전환 주기(ms)

const EMPTY_WIDGET_DATA: SensorWidgetData = { groups: [], locations: [] };

// 백엔드 /sensor/widget 연동 전 mock 으로 미리보기.
// 백엔드가 SensorWidgetRow[] 형식(진동 배열 포함)으로 응답 준비되면 false 로 변경하면 즉시 실데이터 연동된다.
const USE_MOCK = false;

const LEGEND = [
  { key: 'good', label: '좋음' },
  { key: 'caution', label: '주의' },
  { key: 'warning', label: '경고' },
  { key: 'danger', label: '위험' },
];

const SensorStatusWidget = ({ name, refetchInterval, data }: Props) => {
  const { t } = useTranslation();
  const isSiteDashboard = refetchInterval !== undefined;
  const { hCd, sCd } = useRecoilValue(userState);

  // 자동전환(재생) 여부. 대시보드·상황판 모두 기본 재생 ON. 정지 시 좌우 드래그로 수동 페이지 이동.
  const [autoPlay, setAutoPlay] = useState(true);

  // data prop 미주입 시 API 조회: 측정값(GET /sensor/widget) + 위험단계 기준(GET /sensor/range)
  // USE_MOCK 일 때는 API 를 호출하지 않고 mock 으로 렌더(원격 아님).
  const isRemote = data === undefined && !USE_MOCK;
  const queryEnabled = isRemote && !!hCd && !!sCd && sCd !== '00000';

  const fetchSensorRows = async (): Promise<SensorWidgetRow[]> => {
    const res = await apiGet({ path: '/sensor/widget', req: { hCd, sCd } });
    return res?.data?.data ?? [];
  };

  const fetchSensorRange = async (): Promise<SensorRangeMap> => {
    const res = await apiGet({ path: '/sensor/range', req: { hCd, sCd } });
    return buildSensorRangeMap(res?.data?.data?.rangeList ?? []);
  };

  // 센서명(헤더 라벨): /sensor/ulist 의 cdName (ssCd=srCd 기준). subCd 생략 시 전체 코드명 반환.
  const fetchSensorNames = async (): Promise<SensorNameMap> => {
    const res = await apiGet({ path: '/sensor/ulist', req: { hCd, sCd } });
    return buildSensorNameMap(res?.data?.data?.sensorUList ?? []);
  };

  const {
    data: rows,
    isInitialLoading: isLoadingRows,
    isError,
    refetch: refetchRows,
  } = useQuery(['sensorWidgetGet', hCd, sCd], fetchSensorRows, {
    enabled: queryEnabled,
    ...(refetchInterval && { refetchInterval }),
  });

  // 위험단계 기준(단위 포함)·센서명은 자주 바뀌지 않으므로 자동 갱신 없이 캐싱(새로고침 시 함께 refetch)
  const {
    data: rangeMap,
    isInitialLoading: isLoadingRange,
    refetch: refetchRange,
  } = useQuery(['sensorWidgetRangeGet', hCd, sCd], fetchSensorRange, {
    enabled: queryEnabled,
  });

  const {
    data: nameMap,
    isInitialLoading: isLoadingNames,
    refetch: refetchNames,
  } = useQuery(['sensorWidgetNameGet', hCd, sCd], fetchSensorNames, {
    enabled: queryEnabled,
  });

  const effectiveData = useMemo<SensorWidgetData>(() => {
    if (data) return data;
    // mock: 실 API 와 동일한 어댑터를 거쳐 렌더(연동 시 동작 일치 보장)
    if (USE_MOCK) return sensorRowsToWidgetData(SENSOR_WIDGET_MOCK, SENSOR_RANGE_MOCK, {});
    if (!rows) return EMPTY_WIDGET_DATA;
    return sensorRowsToWidgetData(rows, rangeMap ?? {}, nameMap ?? {});
  }, [data, rows, rangeMap, nameMap]);

  // 컨테이너 실측(반응형 폰트 계산용)
  const { ref: bodyRef, height } = useElementSize<HTMLDivElement>();

  // 새로고침: 데이터 재조회 + 첫 페이지로 이동 + fade 재생(시각 피드백)
  const [refreshTick, setRefreshTick] = useState(0);

  // 방어적 정규화 (500/엔벨로프/undefined 유입 시 흰화면 방지)
  const groups = useMemo(() => (Array.isArray(effectiveData?.groups) ? effectiveData.groups : []), [effectiveData]);
  const locations = useMemo(() => (Array.isArray(effectiveData?.locations) ? effectiveData.locations : []), [effectiveData]);

  // ── 페이징 ────────────────────────────────────────────────
  // 가로(센서 종류): 12종 초과 시 그룹을 통째로 묶어 컬럼 페이지로 분할
  const colPages = useMemo(() => paginateGroupsByColumns(groups, MAX_SENSOR_COLS), [groups]);
  const colPageCount = colPages.length;
  // 세로(설치 위치): 5개 초과 시 행 페이지로 분할
  const rowPageCount = Math.max(1, Math.ceil(locations.length / TABLE_PAGE_SIZE));
  // 전체 페이지 = 컬럼 페이지 × 행 페이지. 같은 행 묶음의 컬럼을 먼저 모두 순회한 뒤 다음 행으로.
  const pageCount = colPageCount * rowPageCount;

  const { page, setPage } = usePager({
    pageCount,
    intervalMs: PAGE_INTERVAL,
    enabled: autoPlay, // 재생 중일 때만 자동전환
  });

  const colPage = page % colPageCount;
  const rowPage = Math.floor(page / colPageCount);
  const pageGroups = colPages[colPage] ?? colPages[0];

  // 정지(수동) 모드에서 좌우 드래그로 페이지 이동
  const dragStartX = useRef<number | null>(null);
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (autoPlay) return; // 재생 중에는 수동 이동 비활성
      dragStartX.current = e.clientX;
    },
    [autoPlay]
  );
  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (autoPlay || dragStartX.current === null) return;
      const dx = e.clientX - dragStartX.current;
      dragStartX.current = null;
      if (pageCount <= 1 || Math.abs(dx) < 40) return; // 임계값 미만은 무시
      if (dx < 0) setPage((prev) => (prev + 1) % pageCount); // ← 드래그: 다음
      else setPage((prev) => (prev - 1 + pageCount) % pageCount); // → 드래그: 이전
    },
    [autoPlay, pageCount, setPage]
  );

  const onClickRefresh = useCallback(() => {
    setPage(0);
    setRefreshTick((prev) => prev + 1);
    if (isRemote) {
      refetchRows();
      refetchRange();
      refetchNames();
    }
  }, [setPage, isRemote, refetchRows, refetchRange, refetchNames]);

  const pageLocations = useMemo(() => locations.slice(rowPage * TABLE_PAGE_SIZE, rowPage * TABLE_PAGE_SIZE + TABLE_PAGE_SIZE), [locations, rowPage]);

  // 테이블 폰트: 실측 높이를 본문 행 높이로 환산해 셀 크기에 비례하도록 계산
  const tableFontPx = useMemo(() => {
    if (height <= 0 || pageLocations.length === 0) return undefined;
    const hasGroupedHeader = pageGroups.some((g) => !g.inline && Array.isArray(g.units) && g.units.length > 1);
    const headerWeight = hasGroupedHeader ? 1.7 : 1; // 헤더가 차지하는 행 높이 가중치
    const cellH = height / (pageLocations.length + headerWeight);
    // 셀 높이의 약 25%를 폰트로, 13~22px 범위로 제한
    return Math.max(13, Math.min(22, Math.round(cellH * 0.25)));
  }, [height, pageLocations.length, pageGroups]);

  const hasData = groups.length > 0 && locations.length > 0;
  const loading = isRemote && (isLoadingRows || isLoadingRange || isLoadingNames);

  return (
    <SensorStatusRoot className='sensorStatusWidget'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>{name || t('위치별 센서 종합현황')}</span>
          <div className='title-right'>
            <div className='legend labelSet flex-end'>
              {LEGEND.map((l) => (
                <span key={l.key} className={`chip ${l.key}`}>
                  {t(l.label)}
                </span>
              ))}
            </div>
            {/* 재생/정지 + 새로고침: 상황판(항상 자동전환)에서는 숨기고 대시보드에서만 노출 */}
            {!isSiteDashboard && (
              <div className='widget-btn-group'>
                <PlayButton autoPlay={autoPlay} onClick={() => setAutoPlay((prev) => !prev)} />
                <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                  <span className='material-symbols-rounded'>refresh</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : isRemote && isError ? (
        <IssueGuide type='error' text='센서 데이터를 불러오지 못했습니다' />
      ) : !hasData ? (
        <IssueGuide type='data' text='센서 데이터가 존재하지 않습니다' />
      ) : (
        <div className={`sensor-body${!autoPlay && !isSiteDashboard ? ' manual' : ''}`} ref={bodyRef} onPointerDown={onPointerDown} onPointerUp={onPointerUp}>
          {/* page 가 바뀔 때 remount → 본문(tbody)만 페이드 재생(고정 헤더는 페이드 안 함) */}
          <div className='page-fade' key={`${page}-${refreshTick}`}>
            <SensorTableView groups={pageGroups} locations={pageLocations} fontPx={tableFontPx} />
          </div>
        </div>
      )}
    </SensorStatusRoot>
  );
};

export default SensorStatusWidget;
